import moment from "moment-timezone";
import { isEmpty } from "lodash";

import { RPM_PATIENT_STATUS } from "../../constants";
import Strings from "../../Strings";
import { flatProgramsSubprogramsList, programs, subprograms } from "../../utils/cmsPrograms";
import { defaultCapTypeForRTM, getDefaultActivationMethod, getVitalsValuesFromEnrollment } from "../../utils/enrollmentFields";
import { defaultShipmentMethod, medicationsFromDTO } from "../../utils/rpmPatientFields";
import { PATIENT_ELIGIBILITY_INFO_RESULT, REFRESH_ELIGIBILITY_RESULTS_RESULT, TRIGGER_ELIGIBILITY_CHECK_RESULT } from "../../pages/SuperUser/Patients/redux/constants";
import { ORG_FEATURES, orgFeatureValue } from "../../utils/userPermissions";
import { fieldList, getAddressFromFields } from "../BulkAddPatients/helpers";

export const OnboardStages = {
  onboarding: 'onboarding',
  registration: 'registration',
  rescheduleTelevisit: 'rescheduleTelevisit',
  changeOutreachStatus: 'changeOutreachStatus',
  enrolling: 'enrolling',
  activating: 'activating',
  settingEndDate: 'settingEndDate',
  editPatient: 'editPatient',
  editEnrollment: 'editEnrollment',
};

const getAddressFromRawData = rawPatientImportData => {
  if (!rawPatientImportData || rawPatientImportData?.length < 2) {
    return null;
  }
  const fields = [];
  fieldList(null).address.columnNames.forEach(cn => {
    const field = Object.keys(rawPatientImportData[1]).find(f => f.toLowerCase() === cn);
    if (field) {
      const value = rawPatientImportData[1][field];
      fields.push({field: cn, value: typeof value === 'string' ? value.trim() : typeof value === 'number' ? value.toString() : ''});
    }
  });
  return getAddressFromFields(null, fields);
};

export const getPatientFromProps = (patient, enrollment = patient) => {
  const futureTelevisit = patient.enrollmentSchedules?.find(s => moment(s.enrollAt).isAfter(moment()));
  return {
    ...patient,
    consentForProvider: enrollment?.consentForProvider,
    outreachStatus: enrollment?.outreachStatus,
    outreachSetAt: enrollment?.outreachSetAt,
    dateOfBirth: patient.dateOfBirth ? moment(patient.dateOfBirth, 'YYYY-MM-DD') : '',
    address: (!patient.address || isEmpty(patient.address)) ? getAddressFromRawData(patient.rawPatientImportData) : patient.address,
    nextOutreachAt: enrollment?.nextOutreachAt ? moment.tz(enrollment.nextOutreachAt, patient.timezone) : null,
    conditions: patient.conditionsObject ? Object.keys(patient.conditionsObject) : patient.conditions ? Object.keys(patient.conditions) : [],
    medicalHistory: null,
    transplantDetails: patient.transplantDetails?.length > 0 ? patient.transplantDetails.map(td => ({
      ...td,
      transplantDate: td.transplantDate ? moment(td.transplantDate, 'YYYY-MM-DD') : '',
    })) : [{}],
    centerDetails: patient.centerDetails?.length > 0 ? patient.centerDetails : [{}],
    insurance: patient.insurance?.length > 0 ? patient.insurance : [{}],
    medications: medicationsFromDTO(patient?.medications),
    televisitChecked: !!futureTelevisit,
    initiateEnrollmentByProviderAt: futureTelevisit?.enrollAt ? moment.tz(futureTelevisit.enrollAt, patient.timezone) : null,
    // defaultShipmentMethod: patient.defaultShipmentMethod || defaultShipmentMethod,
    // defaultRtmDeviceModel: patient.defaultRtmDeviceModel || defaultCapTypeForRTM,
  };
};

export const getPatientEnrollmentsFromProps = (patient, stage, orgTimezone) => {
  const supportedProgramsForEnrollment = patient?.patientEnrollment?.supportedPrograms || [];
  const programToSelect = supportedProgramsForEnrollment.length === 1 ? supportedProgramsForEnrollment[0] : null;
  const supportedSubprogramsForEnrollment = patient?.patientEnrollment?.supportedSubprograms || [];
  const subprogramToSelect = supportedSubprogramsForEnrollment.length === 1 ? supportedSubprogramsForEnrollment[0] : null;
  const enrollment_activation_modes = orgFeatureValue(ORG_FEATURES.enrollment_activation_modes);
  const defaultActivationMethod = getDefaultActivationMethod(enrollment_activation_modes);

  if (_.isEmpty(patient?.patientEnrollment?.enrollments)) {
    return [{
      conditionId: patient?.conditions && Object.keys(patient.conditions).length === 1 ? Object.keys(patient.conditions)[0] : null,
      enrollmentEnd: '',
      shipment_method: patient?.defaultShipmentMethod !== "not_set" ? patient?.defaultShipmentMethod : undefined,
      rtm_device_model: patient?.defaultRtmDeviceModel || defaultCapTypeForRTM,
      subprogram: supportedSubprogramsForEnrollment.length === 1 ? supportedSubprogramsForEnrollment[0] : null,
      name: supportedProgramsForEnrollment.length === 1 ? programs[supportedProgramsForEnrollment[0]] : null,
      activation_method: defaultActivationMethod,
    }];
  }
  const fallbackConditionId =
    patient?.conditions && Object.keys(patient.conditions).length === 1 ? Object.keys(patient.conditions)[0] : null;
  const enrollments = Object.entries(patient.patientEnrollment.enrollments).map(([id, v]) => {
    const manualActivationMethodSetByDoctor = v.activationMethod === "Manual" && v.enrollmentStart;
      return {
        ...v,
        enrollmentId: id,
        conditionId: v.conditionId || fallbackConditionId,
        conditionIds: v.conditionIds || [],
        name: v.program ? programs[v.program] : programToSelect ? programs[programToSelect] : null,
        enrollmentStart: v.enrollmentStart
          ? moment(v.enrollmentStart)
          : stage === OnboardStages.activating
          ? moment.tz(orgTimezone).add(14, 'days').startOf('day')
          : '',
          kit_id: v.kitUnguessableId || v.shipment?.kit_id,
          enrollmentEnd: v.enrollmentEnd ? moment(v.enrollmentEnd) : '',
        subprogram: v.subprogram ? subprograms[v.subprogram] : subprogramToSelect ? subprograms[subprogramToSelect] : null,
        isDefault: v.program === patient.patientEnrollment.defaultProgram,
        id: flatProgramsSubprogramsList.find(
          l => l.name === programs[v.program] && l.subprogram === subprograms[v.subprogram],
        )?.id,
        care_provider: v.careProvider?.uuid || null,
        care_provider_org_uuid: v.careProviderOrg?.uuid || null,
        medication: v.medications?.length > 0 ? v.medications[0] : null,
        // Fix for the case where portal set activation method to manual authomatically. In this case, we want to show the default activation method instead
        activation_method: (v.status === RPM_PATIENT_STATUS.activated || manualActivationMethodSetByDoctor || v.activationMethod !== "Manual") ? v.activationMethod : defaultActivationMethod,
        vitals: getVitalsValuesFromEnrollment(v),
        separate_hr_device: v.separateHrDevice || false,
        provider_order_no: v.providerOrderNo || v.shipment?.provider_order_no,
        completion_sms_enabled: v.completionSms || false,
        terminated_reason: v.terminatedReason || null,
      };
    });
  return enrollments;
};


export const getEnrollmentFromProps = (patient, stage, orgTimezone, selectedEnrollmentId) => {
  const enrollments = getPatientEnrollmentsFromProps(patient, stage, orgTimezone);
  if (selectedEnrollmentId) {
    const selectedEnrollment = enrollments.find(e => e.enrollmentId === selectedEnrollmentId);
    if (selectedEnrollment) {
      return selectedEnrollment;
    }
  }
  return enrollments.find(e => e.isDefault) || enrollments[0];
};

const getPatientEnrollmentState = (patient) => {
  if (patient?.enrollmentStates?.length > 0) {
    if (patient?.enrollmentStates?.some(s => s === RPM_PATIENT_STATUS.activated)) {
      return RPM_PATIENT_STATUS.activated;
    }
    if (patient?.enrollmentStates?.some(s => s === RPM_PATIENT_STATUS.enrolled)) {
      return RPM_PATIENT_STATUS.enrolled;
    }
    if (patient?.enrollmentStates?.some(s => s === RPM_PATIENT_STATUS.verified)) {
      return RPM_PATIENT_STATUS.verified;
    }
    if (patient?.enrollmentStates?.some(s => s === RPM_PATIENT_STATUS.onboarded)) {
      return RPM_PATIENT_STATUS.onboarded;
    }
    return patient.enrollmentStates[0];
  }
  return null;
};

export const calcNextStageForPatient = (patient, modalStage) => {
  const currentState = getPatientEnrollmentState(patient) || patient?.status;
  switch (modalStage) {
    case OnboardStages.onboarding:
      return RPM_PATIENT_STATUS.onboarded;
    case OnboardStages.registration:
      return RPM_PATIENT_STATUS.verified;
    case OnboardStages.enrolling:
      return RPM_PATIENT_STATUS.enrolled;
    case OnboardStages.activating:
      return RPM_PATIENT_STATUS.activated;
    case OnboardStages.settingEndDate:
      return RPM_PATIENT_STATUS.ended;
    case OnboardStages.editPatient:
    case OnboardStages.editEnrollment:
      return currentState;
    default:
      return currentState;
  }
};

export const getSuccessEnrollmentNotification = (patient, stage) => {
  switch (stage) {
    case OnboardStages.enrolling:
      return Strings.formatString(Strings.patientEnrolledInfo, patient.patientName);
    case OnboardStages.activating:
      return Strings.formatString(Strings.patientActivatedInfo, patient.patientName);
    case OnboardStages.settingEndDate:
      return Strings.formatString(Strings.endDateSet, patient.patientName);
    default:
      return Strings.formatString(Strings.patientEnrolledInfo, patient.patientName);
  }
};

export const addListItem = (list, setter) => {
  setter(p => ({
    ...p,
    [list]: p[list].concat({}),
  }));
};

export const editListItem = (event, list, index, setter) => {
  const field = event.target.name;
  const value = event.target.value;
  setter(p => ({
    ...p,
    [list]: p[list].map((h, i) => {
      if (i === index) {
        return {
          ...h,
          [field]: value,
        };
      }
      return h;
    }),
  }));
};

export const removeListItem = (list, index, setter) => {
  setter(p => ({
    ...p,
    [list]: p[list].filter((_, i) => i !== index),
  }));
};


const fetchEligibilityInfo = (patient, getPatientEligibilityInfo, setter, showNotification) => {
  getPatientEligibilityInfo(patient.id).then(resp => {
    if (resp.type === PATIENT_ELIGIBILITY_INFO_RESULT) {
      if (setter) {
        setter(resp.response);
      }
    } else {
      showNotification(resp.response?.data?.standard_error?.message, 5000, true);
    }
  });
};

const checkForEligibilityRefresh = async (patient, getPatientEligibilityInfo, refreshEligibilityResults, setter, showNotification) => {
  let checkCompleted = false;
  const delay = ms => new Promise(resolve => { setTimeout(() => resolve(), ms); });
  const maxAttempts = 10;
  let attempts = 0;

  // eslint-disable-next-line no-await-in-loop
  while (!checkCompleted && attempts < maxAttempts) {
    // eslint-disable-next-line no-await-in-loop
    const resp = await refreshEligibilityResults(patient.id);
    if (resp.type === REFRESH_ELIGIBILITY_RESULTS_RESULT && resp.response?.status === "completed") {
      fetchEligibilityInfo(patient, getPatientEligibilityInfo, setter);
      checkCompleted = true;
      break;
    } else if (resp.response?.data?.standard_error) {
      showNotification(resp.response.data.standard_error.message, 5000, true);
      checkCompleted = true;
      break;
    }
    attempts += 1;
    // eslint-disable-next-line no-await-in-loop
    await delay(1000);
  }
};

export const getEligibilityInfo = (patient, getPatientEligibilityInfo, triggerEligibilityCheck, refreshEligibilityResults, setter, showNotification, forceRefresh = false) => {
  if (patient?.eligibilitySummary?.status === "completed" && !forceRefresh) {
    fetchEligibilityInfo(patient, getPatientEligibilityInfo, setter, showNotification);
    return;
  }
  if (patient?.eligibilitySummary?.status === "pending") {
    checkForEligibilityRefresh(patient, getPatientEligibilityInfo, refreshEligibilityResults, setter, showNotification);
    return;
  }

  triggerEligibilityCheck(patient.id).then(resp => {
    showNotification(Strings.eligibilityCheckInProgress);
    if (setter) {
      setter({
        error: null,
        checkInProgress: true,
      });
    }
    if (resp.type === TRIGGER_ELIGIBILITY_CHECK_RESULT) {
      checkForEligibilityRefresh(patient, getPatientEligibilityInfo, refreshEligibilityResults, setter, showNotification);
    } else if (resp.response?.data?.standard_error) {
      showNotification(resp.response.data.standard_error.message, 5000, true);
      if (setter) {
        setter({
          error: resp.response.data.standard_error.message,
        });
      }
    }
  });
};
